import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Repeat, UserX } from 'lucide-react';
import { ACCENT, GREEN, BLUE, PURPLE, TIER_COLORS, Spinner, KpiCard } from './shared';

function PieSection({ title, subtitle, data, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-[#1A1A1A] mb-0.5">{title}</h3>
      <p className="text-[#64748B] text-[10px] mb-4">{subtitle}</p>
      <div className="flex items-center gap-4">
        <ResponsiveContainer width="45%" height={160}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35}>
              {data.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: d.color }} />
              <span className="text-[#64748B]">{d.name}</span>
              <span className="font-bold text-[#1A1A1A]">{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SegmentsTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi.get('/admin/analytics/business?period=month').then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner />;
  const seg = d?.segmentation || {};
  const spendData = [
    { name: 'Alto spendente', value: seg.high_spenders, color: GREEN },
    { name: 'Basso spendente', value: seg.low_spenders, color: ACCENT },
    { name: 'Senza dati', value: seg.no_data_spend, color: '#E5E5E5' },
  ].filter(s => s.value > 0);
  const freqAll = d?.kpi?.total_customers - (seg.high_frequency + seg.recurring);
  const freqData = [
    { name: 'Alta frequenza', value: seg.high_frequency, color: BLUE },
    { name: 'Ricorrente', value: seg.recurring, color: PURPLE },
    { name: 'Occasionale', value: Math.max(freqAll, 0), color: '#E5E5E5' },
  ].filter(s => s.value > 0);
  const tiers = seg.tiers || [];
  const totalTier = tiers.reduce((s, t) => s + t.count, 0);

  return (
    <div className="space-y-6" data-testid="segments-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieSection title="Segmentazione per spesa" subtitle={`Mediana scontrino: \u20AC${seg.spending_median}`} data={spendData} testid="pie-spending" />
        <PieSection title="Segmentazione per frequenza" subtitle="2+ visite/mese = Alta freq. | 10+ visite/anno = Ricorrente" data={freqData} testid="pie-frequency" />
      </div>
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="tier-distribution">
        <h3 className="font-semibold text-[#1A1A1A] mb-4">Distribuzione per Tier</h3>
        <div className="flex items-center gap-6">
          <ResponsiveContainer width="35%" height={180}>
            <PieChart>
              <Pie data={tiers} dataKey="count" nameKey="tier" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2}>
                {tiers.map(e => <Cell key={e.tier} fill={TIER_COLORS[e.tier] || '#888'} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-3">
            {tiers.map(t => {
              const pct = totalTier > 0 ? Math.round(t.count / totalTier * 100) : 0;
              return (
                <div key={t.tier}>
                  <div className="flex justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS[t.tier] || '#888' }} />
                      <span className="text-[#64748B]">{t.tier}</span>
                    </div>
                    <span className="font-bold text-[#1A1A1A]">{t.count} <span className="text-[#64748B] font-normal text-xs">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: TIER_COLORS[t.tier] || '#888' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Retention 30gg" value={`${d?.retention?.rate_30d || 0}%`} icon={Repeat} color={GREEN} testid="seg-ret-30" />
        <KpiCard label="Retention 60gg" value={`${d?.retention?.rate_60d || 0}%`} icon={Repeat} color={BLUE} testid="seg-ret-60" />
        <KpiCard label="Retention 90gg" value={`${d?.retention?.rate_90d || 0}%`} icon={Repeat} color={PURPLE} testid="seg-ret-90" />
        <KpiCard label="Churn rate" value={`${d?.kpi?.churn_rate || 0}%`} icon={UserX} color="#EF4444" sub="Inattivi 60+ gg" testid="seg-churn" />
      </div>
    </div>
  );
}
